import { withClientClock } from '@common/services/client-clock-fields';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { DBUserMessageThunks } from '@common-reducers/DBServiceThunks';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import * as XLSX from 'xlsx';
import { readSpreadsheet } from '@common/utils/decode-csv-bytes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface ExcelImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

interface ImportResult {
  success: number;
  failed: number;
  errors: string[];
}

export const ExcelImportDialog = ({ open, onClose, onImportSuccess }: ExcelImportDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [progress, setProgress] = useState(0);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
    }
  }, []);

  const parseExcelToMessages = async (file: File): Promise<Partial<UserMessage>[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = readSpreadsheet(XLSX, data);
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);

          const messages: Partial<UserMessage>[] = jsonData.map((row: any) => {
            const contactMobile = row['Contact Mobile'] || row['contactMobile'];
            const whatsappId = row['WhatsApp ID'] || row['whatsappId'];
            
            return {
              contactName: row['Contact Name'] || row['contactName'],
              message: row['Message'] || row['message'],
              dueDate: row['Due Date'] ? new Date(row['Due Date']) : 
                       row['Due Time'] ? new Date(row['Due Time']) : undefined,
              status: row['Status'] || row['status'] || 'pending',
              contactList: (contactMobile || whatsappId) ? [{
                mobile: contactMobile,
                id: whatsappId || contactMobile, // WhatsApp ID is the primary identifier
                firstName: row['Contact First Name'] || '',
                lastName: row['Contact Last Name'] || '',
                name: row['Contact Name'] || '',
                formattedTitle: row['Contact Name'] || ''
              }] : undefined,
              verifyContact: row['Verify Contact'] === 'Yes' || row['verifyContact'] === true,
              isRecurring: row['Is Recurring'] === 'Yes' || row['isRecurring'] === true,
              sentWith: row['Sent With'] || 'whatsapp',
              retryCount: parseInt(row['Retry Count']) || 0,
              campaignId: row['Campaign ID'] || undefined,
            };
          });

          resolve(messages);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  };

  const handleImport = useCallback(async () => {
    if (!selectedFile) return;

    setIsImporting(true);
    setProgress(0);
    const result: ImportResult = {
      success: 0,
      failed: 0,
      errors: []
    };

    try {
      const messages = await parseExcelToMessages(selectedFile);
      const total = messages.length;

      for (let i = 0; i < messages.length; i++) {
        const message = messages[i];
        
        try {
          // Validate required fields
          if (!message.message || (!message.contactName && !message.contactList?.[0]?.mobile)) {
            throw new Error('Missing required fields: message and contact information');
          }

          await dispatch(DBUserMessageThunks.create(withClientClock(message) as any)).unwrap();
          result.success++;
        } catch (error: any) {
          result.failed++;
          result.errors.push(`Row ${i + 2}: ${error.message || 'Unknown error'}`);
        }

        setProgress(Math.round(((i + 1) / total) * 100));
      }

      setImportResult(result);
      
      if (result.success > 0) {
        onImportSuccess();
      }
    } catch (error: any) {
      result.errors.push(`File parsing error: ${error.message}`);
      setImportResult(result);
    } finally {
      setIsImporting(false);
    }
  }, [selectedFile, dispatch, onImportSuccess]);

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setImportResult(null);
    setProgress(0);
    onClose();
  }, [onClose]);

  const handleDownloadTemplate = useCallback(() => {
    const templateData = [
      {
        'Contact Name': 'John Doe',
        'Contact Mobile': '+1234567890',
        'Contact First Name': 'John',
        'Contact Last Name': 'Doe',
        'WhatsApp ID': '1234567890@c.us',
        'Message': 'Hello! This is a sample message.',
        'Due Date': new Date().toLocaleDateString(),
        'Due Time': new Date().toLocaleString(),
        'Status': 'pending',
        'Verify Contact': 'Yes',
        'Is Recurring': 'No',
        'Sent With': 'whatsapp',
        'Retry Count': 0,
        'Campaign ID': '',
        'Send Failure Reason': '',
        'Created At': new Date().toLocaleString(),
        'Updated At': new Date().toLocaleString(),
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');

    XLSX.writeFile(wb, 'user-messages-import-template.xlsx');
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('importMessages.dialog.title')}</DialogTitle>
          <DialogDescription>
            {t('importMessages.dialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Download Template Button */}
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            className="w-full flex items-center justify-center gap-2"
          >
            <FileSpreadsheet className="h-4 w-4" />
            {t('importMessages.dialog.downloadTemplate')}
          </Button>

          {/* File Upload */}
          <div className="border-2 border-dashed rounded-lg p-6 text-center" style={{ borderColor: 'var(--color-border)' }}>
            <input
              type="file"
              id="file-upload"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isImporting}
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer flex flex-col items-center gap-2"
            >
              <Upload className="h-10 w-10" style={{ color: 'var(--color-text-secondary)' }} />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                  {selectedFile ? selectedFile.name : t('importMessages.dialog.clickToUpload')}
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {t('importMessages.dialog.supportedFormats')}
                </p>
              </div>
            </label>
          </div>

          {/* Import Progress */}
          {isImporting && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>{t('importMessages.dialog.importing')}</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}

          {/* Import Results */}
          {importResult && (
            <div className="space-y-2">
              {importResult.success > 0 && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription>
                    {t('importMessages.dialog.successMessage', { count: importResult.success })}
                  </AlertDescription>
                </Alert>
              )}

              {importResult.failed > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {t('importMessages.dialog.failedMessage', { count: importResult.failed })}
                    {importResult.errors.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer font-medium">{t('importMessages.dialog.viewErrors')}</summary>
                        <ul className="mt-2 space-y-1 text-xs">
                          {importResult.errors.slice(0, 5).map((error, idx) => (
                            <li key={idx}>• {error}</li>
                          ))}
                          {importResult.errors.length > 5 && (
                            <li>... and {importResult.errors.length - 5} more</li>
                          )}
                        </ul>
                      </details>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isImporting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleImport}
            disabled={!selectedFile || isImporting}
          >
            {isImporting ? t('importMessages.dialog.importingButton') : t('importMessages.dialog.importButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

